import { format, subDays } from 'date-fns'
import type { TweCategory } from 'api/generated'
import type { MostClickedFilters } from 'types/twe'

// Radix Select has no empty value, so "nessun filtro" needs a sentinel
export const ALL = 'all'

export const PERIODS = [
  { key: '7', label: '7 giorni', days: 7 },
  { key: '30', label: '30 giorni', days: 30 },
  { key: '90', label: '90 giorni', days: 90 },
  { key: 'sempre', label: 'Sempre', days: null },
] as const

export type PeriodKey = (typeof PERIODS)[number]['key'] | 'custom'

export interface FilterState {
  period: PeriodKey
  dateFrom?: Date
  dateTo?: Date
  limit: string
  categoryId: string
  brand: string
  gender: string
  onSale: boolean
  discountable: boolean
  minPrice: string
  maxPrice: string
  q: string
}

export const initialFilters: FilterState = {
  period: '30',
  limit: '10',
  categoryId: ALL,
  brand: ALL,
  gender: ALL,
  onSale: false,
  discountable: false,
  minPrice: '',
  maxPrice: '',
  q: '',
}

type PeriodState = Pick<FilterState, 'period' | 'dateFrom' | 'dateTo'>

const periodDates = (f: PeriodState) => {
  if (f.period === 'custom') return { from: f.dateFrom, to: f.dateTo }
  const days = PERIODS.find((p) => p.key === f.period)?.days
  if (!days) return {} // "Sempre": no window, the API returns all-time clicks
  return { from: subDays(new Date(), days - 1), to: new Date() }
}

/** The click window as API params; "Sempre" sends neither date. */
export const periodParams = (f: PeriodState) => {
  const { from, to } = periodDates(f)
  return {
    date_from: from ? format(from, 'yyyy-MM-dd') : undefined,
    date_to: to ? format(to, 'yyyy-MM-dd') : undefined,
  }
}

// on_sale/discountable are ignored by the API unless truthy, so only send them when on
export const toParams = (f: FilterState): MostClickedFilters => {
  return {
    limit: Number(f.limit),
    ...periodParams(f),
    category_id: f.categoryId === ALL ? undefined : Number(f.categoryId),
    brand: f.brand === ALL ? undefined : f.brand,
    gender:
      f.gender === ALL ? undefined : (f.gender as MostClickedFilters['gender']),
    on_sale: f.onSale || undefined,
    discountable: f.discountable || undefined,
    min_price: f.minPrice ? Number(f.minPrice) : undefined,
    max_price: f.maxPrice ? Number(f.maxPrice) : undefined,
    q: f.q || undefined,
  }
}

export interface FilterChip {
  key: keyof FilterState
  label: string
}

/** The product-side filters, as removable chips. Period and limit are not chips: they live in the bar. */
export const productChips = (
  f: FilterState,
  categories: TweCategory[]
): FilterChip[] => {
  const chips: FilterChip[] = []
  if (f.categoryId !== ALL)
    chips.push({
      key: 'categoryId',
      label:
        categories.find((c) => String(c.id) === f.categoryId)?.name ??
        'Categoria',
    })
  if (f.brand !== ALL) chips.push({ key: 'brand', label: f.brand })
  if (f.gender !== ALL) chips.push({ key: 'gender', label: f.gender })
  if (f.onSale) chips.push({ key: 'onSale', label: 'In saldo' })
  if (f.discountable)
    chips.push({ key: 'discountable', label: 'Coupon premium' })
  if (f.minPrice) chips.push({ key: 'minPrice', label: `da €${f.minPrice}` })
  if (f.maxPrice)
    chips.push({ key: 'maxPrice', label: `fino a €${f.maxPrice}` })
  if (f.q) chips.push({ key: 'q', label: `"${f.q}"` })
  return chips
}

export const periodLabel = (f: PeriodState) => {
  if (f.period === 'custom') {
    const from = f.dateFrom ? format(f.dateFrom, 'd MMM') : '—'
    const to = f.dateTo ? format(f.dateTo, 'd MMM') : 'oggi'
    return `${from} – ${to}`
  }
  return PERIODS.find((p) => p.key === f.period)?.label ?? ''
}
